'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface ForgotFormData {
  email: string;
}

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors },
  } = useForm<ForgotFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);

      if (!result?.user) {
        toast.error('Login failed. Please check your credentials.');
        return;
      }

      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, first_name, full_name')
        .eq('id', result.user.id)
        .single();

      const role = profile?.role || 'customer';

      if (role === 'admin' || role === 'super_admin') {
        toast.success('Welcome back, Admin!');
        router.push('/admin-dashboard');
      } else {
        const name =
          profile?.first_name ||
          profile?.full_name?.split(' ')[0] ||
          result.user.user_metadata?.first_name ||
          'there';
        toast.success(`Welcome back, ${name}!`);
        router.push('/customer-dashboard');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onForgotSubmit = async (data: ForgotFormData) => {
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://capitalcitybnt.online';
      await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${siteUrl}/reset-password`,
      });
      setForgotEmail(data.email);
      setForgotSent(true);
      startResendCooldown();
    } catch {
      setForgotEmail(data.email);
      setForgotSent(true);
      startResendCooldown();
    } finally {
      setForgotLoading(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !forgotEmail) return;
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://capitalcitybnt.online';
      await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${siteUrl}/reset-password`,
      });
      toast.success('Password reset link resent. Please check your inbox.');
      startResendCooldown();
    } catch {
      toast.success('If that email exists, a new reset link has been sent.');
      startResendCooldown();
    } finally {
      setForgotLoading(false);
    }
  };

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => { if (resendTimerRef.current) clearInterval(resendTimerRef.current); };
  }, []);

  // Forgot password sent confirmation
  if (showForgot && forgotSent) {
    return (
      <div className="space-y-4 text-center">
        <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto">
          <CheckCircle size={24} className="text-success" />
        </div>
        <div>
          <h3 className="text-base font-700 text-foreground mb-1">Check Your Email</h3>
          <p className="text-sm text-muted-foreground">
            If an account exists for that email address, you will receive a password reset link shortly.
          </p>
          {forgotEmail && (
            <p className="text-xs text-muted-foreground mt-1">
              Sent to: <span className="font-600 text-foreground">{forgotEmail}</span>
            </p>
          )}
        </div>
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || forgotLoading}
            className="w-full flex items-center justify-center gap-2 py-2 border border-border rounded-lg text-sm font-500 text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {forgotLoading ? (
              <><Loader2 size={14} className="animate-spin" />Sending...</>
            ) : resendCooldown > 0 ? (
              `Resend available in ${resendCooldown}s`
            ) : (
              'Resend Reset Link'
            )}
          </button>
          <button
            type="button"
            onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); setResendCooldown(0); if (resendTimerRef.current) clearInterval(resendTimerRef.current); }}
            className="flex items-center gap-2 text-sm text-accent hover:underline mx-auto"
          >
            <ArrowLeft size={14} />Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Forgot password form
  if (showForgot) {
    return (
      <div className="space-y-4">
        <div>
          <button
            type="button"
            onClick={() => setShowForgot(false)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft size={14} />Back to Sign In
          </button>
          <h3 className="text-base font-700 text-foreground mb-1">Reset Password</h3>
          <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
        </div>
        <form onSubmit={handleForgotSubmit(onForgotSubmit)} className="space-y-4">
          <div>
            <label htmlFor="forgot-email" className="block text-sm font-600 text-foreground mb-1.5">
              Email address
            </label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                {...registerForgot('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                className={`w-full pl-9 pr-3.5 py-2.5 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                  forgotErrors.email ? 'border-danger' : 'border-border hover:border-muted-foreground'
                }`}
                placeholder="you@capitalcitybnt.online"
              />
            </div>
            {forgotErrors.email && (
              <p className="mt-1 text-xs text-danger">{forgotErrors.email.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={forgotLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {forgotLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>
      </div>
    );
  }

  // Main login form
  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block text-sm font-600 text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
            className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
              errors.email ? 'border-danger' : 'border-border hover:border-muted-foreground'
            }`}
            placeholder="you@capitalcitybnt.online"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-600 text-foreground">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-xs text-accent hover:underline font-500"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors pr-10 ${
                errors.password ? 'border-danger' : 'border-border hover:border-muted-foreground'
              }`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="remember-me"
            type="checkbox"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-border accent-primary"
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground">
            Keep me signed in for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In to Capital City BNT'
          )}
        </button>
      </form>
    </div>
  );
}